import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import type { QuizAttemptResult, QuizQuestionPublic, QuizSubmission } from '@xsc/shared';
import { api } from '../lib/api-client.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { Badge } from '../components/ui/badge.js';

/**
 * Quiz player (Task #17).
 *
 * Server returns options WITHOUT `is_correct`; selected answers are POSTed
 * and the server returns a graded attempt. Pass triggers automatic
 * certificate issuance check on the backend.
 */
export function TrainingQuizPage(): JSX.Element {
  const { moduleId = '' } = useParams<{ moduleId: string }>();
  const qc = useQueryClient();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<QuizAttemptResult | null>(null);

  const quizQ = useQuery({
    queryKey: ['training', 'quiz', moduleId],
    queryFn: () => api.get<{ questions: QuizQuestionPublic[] }>(`/v1/training/modules/${encodeURIComponent(moduleId)}/quiz`),
    enabled: Boolean(moduleId),
  });

  const submit = useMutation({
    mutationFn: (body: QuizSubmission) =>
      api.post<QuizAttemptResult>(
        `/v1/training/modules/${encodeURIComponent(moduleId)}/quiz/attempt`,
        body,
      ),
    onSuccess: async (data) => {
      setResult(data);
      await qc.invalidateQueries({ queryKey: ['training', 'modules'] });
      await qc.invalidateQueries({ queryKey: ['training', 'certificate'] });
    },
  });

  if (quizQ.isLoading) return <p className="text-ink-muted">Loading quiz…</p>;
  if (quizQ.isError) return <p className="text-danger">Failed to load quiz.</p>;

  const questions = quizQ.data?.questions ?? [];
  if (questions.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-ink-muted">This module has no quiz.</p>
        <Link to={`/training/${moduleId}`}><Button variant="secondary">Back to module</Button></Link>
      </div>
    );
  }

  const answered = questions.filter((q) => answers[q.id]).length;
  const canSubmit = answered === questions.length && !submit.isPending && !result;

  function submitQuiz(): void {
    const body: QuizSubmission = {
      answers: questions.map((q) => ({
        question_id: q.id,
        selected_option_id: answers[q.id] ?? '',
      })),
    };
    submit.mutate(body);
  }

  function retake(): void {
    setAnswers({});
    setResult(null);
  }

  // ─── Results screen ────────────────────────────────────────────────────
  if (result) {
    // Server only reveals the correct option for questions the user
    // actually answered — empty answers stay hidden so the key can't be
    // dumped via a throwaway attempt.
    const earnedByQ = new Map(result.graded_answers.map((a) => [a.question_id, a]));
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h1 className="text-2xl font-semibold">Quiz results</h1>
          <Link to={`/training/${moduleId}`} className="text-sm text-ink-muted hover:text-ink">
            ← Back to module
          </Link>
        </div>
        <Card>
          <CardBody className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-3xl font-semibold">{Math.round(result.score)}%</div>
              <div className="text-sm text-ink-muted">
                {result.points_earned} of {result.points_possible} points · pass ≥{' '}
                {result.passing_score}%
              </div>
            </div>
            <div>
              {result.passed ? (
                <Badge tone="success">Passed</Badge>
              ) : (
                <Badge tone="danger">Did not pass</Badge>
              )}
            </div>
            <div className="flex gap-2">
              {!result.passed && <Button onClick={retake}>Retake quiz</Button>}
              <Link to="/training">
                <Button variant="secondary">All training</Button>
              </Link>
            </div>
          </CardBody>
        </Card>
        <div className="space-y-3">
          {questions.map((q, i) => {
            const graded = earnedByQ.get(q.id);
            const correctId = graded?.correct_option_id ?? null;
            return (
              <Card key={q.id}>
                <CardHeader>
                  <CardTitle>
                    {i + 1}. {q.question}
                  </CardTitle>
                  {graded?.is_correct ? (
                    <Badge tone="success">Correct</Badge>
                  ) : (
                    <Badge tone="danger">Incorrect</Badge>
                  )}
                </CardHeader>
                <CardBody className="space-y-2">
                  <ul className="space-y-1 text-sm">
                    {q.options.map((o) => {
                      const isSelected = graded?.selected_option_id === o.id;
                      const isRight = o.id === correctId;
                      const tone = isRight
                        ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
                        : isSelected
                        ? 'border-red-400 bg-red-50 dark:bg-red-900/20'
                        : 'border-border';
                      return (
                        <li key={o.id} className={`rounded border px-2 py-1 ${tone}`}>
                          {isSelected && <span className="mr-2">●</span>}
                          {isRight && !isSelected && <span className="mr-2">✓</span>}
                          {o.option_text}
                        </li>
                      );
                    })}
                  </ul>
                  {q.explanation && (
                    <p className="text-xs text-ink-muted italic">{q.explanation}</p>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── Quiz form ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <Link to={`/training/${moduleId}`} className="text-sm text-ink-muted hover:text-ink">
            ← Back to module
          </Link>
          <h1 className="text-2xl font-semibold mt-1">Module quiz</h1>
        </div>
        <div className="text-sm text-ink-muted">
          {answered} / {questions.length} answered
        </div>
      </div>

      <div className="space-y-3">
        {questions.map((q, i) => (
          <Card key={q.id}>
            <CardHeader>
              <CardTitle>
                {i + 1}. {q.question}
              </CardTitle>
            </CardHeader>
            <CardBody>
              <fieldset className="space-y-2">
                <legend className="sr-only">{q.question}</legend>
                {q.options.map((o) => {
                  const id = `q-${q.id}-${o.id}`;
                  return (
                    <label
                      key={o.id}
                      htmlFor={id}
                      className="flex items-start gap-2 rounded border border-border px-3 py-2 cursor-pointer hover:bg-surface-alt"
                    >
                      <input
                        id={id}
                        type="radio"
                        name={q.id}
                        className="mt-1"
                        checked={answers[q.id] === o.id}
                        onChange={() => setAnswers((p) => ({ ...p, [q.id]: o.id }))}
                      />
                      <span className="text-sm">{o.option_text}</span>
                    </label>
                  );
                })}
              </fieldset>
            </CardBody>
          </Card>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <Link to={`/training/${moduleId}`}>
          <Button variant="secondary">Cancel</Button>
        </Link>
        <Button disabled={!canSubmit} onClick={submitQuiz}>
          {submit.isPending ? 'Submitting…' : 'Submit quiz'}
        </Button>
      </div>
      {submit.isError && (
        <p className="text-danger text-sm">
          Submission failed. Please check your connection and try again.
        </p>
      )}
    </div>
  );
}
